/**
 * Anonymous ID Utility
 *
 * Manages client-side anonymous user identification for tracking
 * non-logged in user game plays. Uses localStorage for persistence.
 */

const ANONYMOUS_ID_KEY = 'anonymous_id';

/**
 * Generate a UUID v4 (random UUID)
 * @returns {string} UUID v4 string
 */
function generateUUIDv4() {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Check if localStorage is available
 * @returns {boolean} True if localStorage is available
 */
function isLocalStorageAvailable() {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Get or create an anonymous ID for the current browser/device
 * Persists across browser sessions via localStorage
 * @returns {string} Anonymous ID (UUID v4)
 */
export function getAnonymousId() {
  if (!isLocalStorageAvailable()) {
    // Fallback: generate a session-only ID if localStorage isn't available
    console.warn('localStorage not available, using session-only anonymous ID');
    return generateUUIDv4();
  }

  try {
    // Try to get existing ID from localStorage
    let anonymousId = localStorage.getItem(ANONYMOUS_ID_KEY);

    // If no ID exists, generate and store a new one
    if (!anonymousId) {
      anonymousId = generateUUIDv4();
      localStorage.setItem(ANONYMOUS_ID_KEY, anonymousId);
    }

    // Validate that the stored ID is a valid UUID v4
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(anonymousId)) {
      // Invalid format, generate a new one
      console.warn('Invalid anonymous ID format found, generating new one');
      anonymousId = generateUUIDv4();
      localStorage.setItem(ANONYMOUS_ID_KEY, anonymousId);
    }

    return anonymousId;
  } catch (error) {
    console.error('Error accessing localStorage for anonymous ID:', error);
    // Return a session-only ID as fallback
    return generateUUIDv4();
  }
}

/**
 * Clear the stored anonymous ID from localStorage
 * Useful when a user creates an account or logs in
 * @returns {boolean} True if successfully cleared, false otherwise
 */
export function clearAnonymousId() {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    localStorage.removeItem(ANONYMOUS_ID_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing anonymous ID from localStorage:', error);
    return false;
  }
}

/**
 * Check if an anonymous ID currently exists in storage
 * @returns {boolean} True if an anonymous ID exists
 */
export function hasAnonymousId() {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    const anonymousId = localStorage.getItem(ANONYMOUS_ID_KEY);
    return !!anonymousId;
  } catch (error) {
    console.error('Error checking for anonymous ID:', error);
    return false;
  }
}

/**
 * Regenerate a new anonymous ID (replaces existing one)
 * @returns {string} New anonymous ID
 */
export function regenerateAnonymousId() {
  if (!isLocalStorageAvailable()) {
    return generateUUIDv4();
  }

  try {
    const newId = generateUUIDv4();
    localStorage.setItem(ANONYMOUS_ID_KEY, newId);
    return newId;
  } catch (error) {
    console.error('Error regenerating anonymous ID:', error);
    return generateUUIDv4();
  }
}

export default {
  getAnonymousId,
  clearAnonymousId,
  hasAnonymousId,
  regenerateAnonymousId
};